import {
  agregarPassosPorDia,
  normalizarRegistro,
  varrerRegistros,
  type ObservacaoNormalizada,
} from "@mostarda/core";
import { abrirEntrada, lerIndice, ZipInvalido, type EntradaZip } from "./zip.js";

/**
 * Importação do export do app Saúde — o histórico inteiro da Laís, processado
 * NO IPHONE DELA.
 *
 * O plano gratuito do Cloudflare Workers dá 10 ms de CPU por requisição. O XML
 * passa de 100 MB. Não dá para parsear no servidor, nem em pedaços, porque o
 * limite é por requisição e não por corpo. Então o telefone parseia, o servidor
 * recebe observação pronta.
 *
 * O efeito colateral é a melhor parte: o XML bruto — que tem tudo que o app
 * Saúde sabe dela, inclusive o que a gente nunca pediu — não sai do aparelho.
 * Sobem só as métricas que o `TIPOS_APPLE` reconhece.
 */

const TAMANHO_LOTE = 500;

export interface Progresso {
  fase: "abrindo" | "lendo" | "enviando" | "pronto";
  bytesLidos: number;
  bytesTotais: number;
  registrosLidos: number;
  enviadas: number;
  duplicadas: number;
  /** Reconhecidas pelo scanner mas fora do `TIPOS_APPLE`. */
  ignoradas: number;
}

export interface ResumoImportacao {
  registrosLidos: number;
  enviadas: number;
  duplicadas: number;
  ignoradas: number;
  retomadaDe: number;
}

export interface RespostaLote {
  inserted: number;
  duplicates: number;
}

export interface OpcoesImportacao {
  enviarLote: (itens: ObservacaoNormalizada[], timezone: string) => Promise<RespostaLote>;
  onProgresso?: (p: Progresso) => void;
  sinal?: AbortSignal;
  /** Fuso do aparelho. Injetável para o teste não depender do relógio da máquina. */
  timezone?: string;
}

/* ─────────────────────────── Retomada ─────────────────────────── */

/**
 * A importação é retomável porque ela VAI ser interrompida: são centenas de
 * lotes num celular, e basta a tela apagar. A chave inclui o tamanho do arquivo
 * para que um export novo não seja confundido com o antigo e comece pela metade.
 */
const chaveRetomada = (arquivo: File | Blob): string => {
  const nome = arquivo instanceof File ? arquivo.name : "export";
  return `mostarda:import:${nome}:${arquivo.size}`;
};

function lerRetomada(arquivo: File | Blob): number {
  try {
    const v = Number(localStorage.getItem(chaveRetomada(arquivo)));
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}

function gravarRetomada(arquivo: File | Blob, n: number): void {
  try {
    localStorage.setItem(chaveRetomada(arquivo), String(n));
  } catch {
    /* modo privado do Safari nega escrita; a importação segue, só não retoma */
  }
}

export function limparRetomada(arquivo: File | Blob): void {
  try {
    localStorage.removeItem(chaveRetomada(arquivo));
  } catch {
    /* idem */
  }
}

/* ─────────────────────────── Abertura ─────────────────────────── */

/**
 * Acha o XML dentro do zip.
 *
 * O nome depende do idioma do iPhone: "exportar.xml" em português,
 * "export.xml" em inglês. E o `export_cda.xml`, que vem junto, é o registro
 * clínico em formato HL7 CDA — outro schema inteiro, sem `<Record>`. Parsear
 * ele daria zero observações e nenhuma explicação.
 */
export function acharXml(entradas: readonly EntradaZip[]): EntradaZip | null {
  const candidatos = entradas.filter((e) => {
    const base = e.nome.split("/").pop()?.toLowerCase() ?? "";
    return base.endsWith(".xml") && !base.includes("cda") && !e.nome.startsWith("__MACOSX/");
  });
  return (
    candidatos.find((e) => /^(export|exportar)\.xml$/.test(e.nome.split("/").pop()!.toLowerCase())) ??
    // Sem nome conhecido, o maior é o certo: o XML de saúde é ordens de
    // grandeza maior que qualquer outro arquivo do pacote.
    candidatos.sort((a, b) => b.tamanhoOriginal - a.tamanhoOriginal)[0] ??
    null
  );
}

async function abrirXml(
  arquivo: File | Blob,
): Promise<{ stream: ReadableStream<Uint8Array>; bytes: number }> {
  const cabecalho = new Uint8Array(await arquivo.slice(0, 4).arrayBuffer());
  const ehZip = cabecalho[0] === 0x50 && cabecalho[1] === 0x4b;

  if (!ehZip) {
    // Alguém pode descompactar antes e mandar o .xml direto. Aceitar é de graça.
    return { stream: arquivo.stream() as ReadableStream<Uint8Array>, bytes: arquivo.size };
  }

  const entradas = await lerIndice(arquivo);
  const alvo = acharXml(entradas);
  if (!alvo) throw new ZipInvalido("Não achei o XML de saúde dentro do zip.");
  return { stream: await abrirEntrada(arquivo, alvo), bytes: alvo.tamanhoOriginal };
}

/* ─────────────────────────── Importação ─────────────────────────── */

export async function importarExportSaude(
  arquivo: File | Blob,
  opcoes: OpcoesImportacao,
): Promise<ResumoImportacao> {
  const timezone =
    opcoes.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "America/Sao_Paulo";
  const retomadaDe = lerRetomada(arquivo);

  const estado: Progresso = {
    fase: "abrindo",
    bytesLidos: 0,
    bytesTotais: 0,
    registrosLidos: 0,
    enviadas: retomadaDe,
    duplicadas: 0,
    ignoradas: 0,
  };
  const avisar = () => opcoes.onProgresso?.({ ...estado });
  avisar();

  const { stream, bytes } = await abrirXml(arquivo);
  estado.bytesTotais = bytes;
  estado.fase = "lendo";
  avisar();

  // Mesmo desencontro de variância da lib.dom explicado em zip.ts.
  const leitor = stream
    .pipeThrough(
      new TextDecoderStream("utf-8") as unknown as ReadableWritablePair<string, Uint8Array>,
    )
    .getReader();
  const decodificadorBytes = new TextEncoder();

  let resto = "";
  let lote: ObservacaoNormalizada[] = [];
  /** Passos ficam de fora do lote: viram um total por dia, no fim. */
  const passos: ObservacaoNormalizada[] = [];
  /** Quantas observações já foram EMITIDAS — inclusive as puladas na retomada. */
  let emitidas = 0;

  const despachar = async () => {
    if (lote.length === 0) return;
    estado.fase = "enviando";
    avisar();
    const r = await opcoes.enviarLote(lote, timezone);
    estado.enviadas += r.inserted;
    estado.duplicadas += r.duplicates;
    gravarRetomada(arquivo, emitidas);
    lote = [];
    estado.fase = "lendo";
    avisar();
  };

  try {
    for (;;) {
      if (opcoes.sinal?.aborted) throw new DOMException("Importação cancelada.", "AbortError");
      const { done, value } = await leitor.read();
      if (done) break;

      estado.bytesLidos += decodificadorBytes.encode(value).length;
      const varredura = varrerRegistros(resto + value);
      resto = varredura.resto;
      estado.registrosLidos += varredura.registros.length;

      for (const bruto of varredura.registros) {
        const obs = normalizarRegistro(bruto);
        if (!obs) {
          estado.ignoradas++;
          continue;
        }
        if (obs.type === "steps") {
          passos.push(obs);
          continue;
        }
        emitidas++;
        // Retomada: o parse é rápido, a rede é que não é. Reparsear e pular o
        // que já foi aceito custa segundos; reenviar tudo custa minutos.
        if (emitidas <= retomadaDe) continue;
        lote.push(obs);
        if (lote.length >= TAMANHO_LOTE) await despachar();
      }
      avisar();
    }

    // Os passos só podem ser somados depois de ver o arquivo inteiro: eles vêm
    // em centenas de fragmentos por dia, e o dia só fecha no fim.
    const totais = agregarPassosPorDia(passos, timezone);
    for (const t of totais) {
      lote.push(t);
      if (lote.length >= TAMANHO_LOTE) await despachar();
    }
    await despachar();
  } finally {
    await leitor.cancel().catch(() => {});
  }

  limparRetomada(arquivo);
  estado.fase = "pronto";
  avisar();

  return {
    registrosLidos: estado.registrosLidos,
    enviadas: estado.enviadas,
    duplicadas: estado.duplicadas,
    ignoradas: estado.ignoradas,
    retomadaDe,
  };
}
